<template lang="pug">
v-expansion-panel(ref="panel")
    v-snackbar( v-model="snackBar" :color="snackBarColor" ) {{ snackBarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackBar = false") {{ $t('buttons.closeButtonLabel') }}
    v-expansion-panel-header(expand-icon="fal fa-chevron-down")
        template(v-slot:default="{open}")
            v-row(v-if="!open" no-gutters)
                v-col(cols="1"  xl="2" lg="2" md="2")
                    strong {{slotItem.code}}
                v-col.text-start.pl-16(cols="6" xl="7" lg="7" md="7")
                    span.ml-3 {{slotItem.title}}
                v-col.text-end.pr-4(cols="4" xl="3" lg="3" md="3" v-if="slotItem.referencePrice > 0")
                    span.ml-3 {{slotItem.referencePrice}} €
            v-row(v-else)
                v-col
                    strong {{$t('settingsPage.offerStructure.slotEditorHeaderLabel')}}
    v-expansion-panel-content
        v-form(ref="form" v-model="isFormValid" :disabled="isSaveInProgress" max-width="600px")
            v-row.ma-0(no-gutters)
                v-col(cols="12")
                  v-text-field(outlined v-model="name" :label="$t('settingsPage.offerStructure.nameLabel')" autofocus)
            v-row.ma-0(no-gutters)
                v-col.pr-2(cols="6")
                    v-text-field(outlined v-model="code" :label="$t('settingsPage.offerStructure.codeLabel')")
                v-col(cols="6")
                    v-text-field(
                        v-model="referencePrice" :label="$t('settingsPage.offerStructure.referencePriceFieldLabel')" outlined
                        )
            v-row.ma-0.align-self-center
              v-col.pl-0.pr-0(cols="12" align="center" )
                v-select.mb-0.text-center(
                  outlined
                  height="56px"
                  :items="tagList"
                  :label="$t('settingsPage.labelLabel')"
                  item-text="title"
                  item-value="id"
                  v-model="selections"
                  color="primary"
                  multiple
                  append-icon="fas fa-chevron-down"
                  chips
                  return-object
                )
                  template( v-slot:selection="{ item, index }")
                    v-chip()
                      v-icon.mr-4(left) {{getIcon(item.iconClass)}}
                      span {{item.title}}
                  template(v-slot:item="{active, item, attrs, on }")
                    v-list-item(v-on="on" v-bind="attrs" v-slot:default="{ active }")
                      v-list-item-action
                        v-checkbox(
                          :input-value="active"
                          on-icon="fas fa-check-square"
                          off-icon="far fa-square" )
                      v-list-item-content
                        v-list-item-title
                          v-icon.mr-4 {{getIcon(item.iconClass)}}
                          span {{item.title}}
            v-row(no-gutters)
                v-col.d-flex.justify-end
                    v-btn(color="primary" :loading="isSaveInProgress" @click="onSubmitButtonClick" :disabled="!isFormValid") {{$t('settingsPage.offerStructure.slotEditorSaveButtonLabel')}}

</template>

<script lang="ts">
import Vue from 'vue'
import {Component, Prop} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import {TranslateResult} from 'vue-i18n'
import i18n from '@/i18n'
import DailyMenuOption from "@/model/dailymenu/DailyMenuOption";
import {UPDATE_SLOT} from "@/store/action-types";
import MenuGroupList from "@/model/menu/MenuGroupList";
import type {SlotCollectionResponse} from "@/types/service-response";
import {EditSlotPayload} from "@/types/payload";
import Tag from "@/model/tennant/Tag";

function valueMustBeNumber(v: string): boolean | TranslateResult {
  if (v) {
    const parsed = parseFloat(v)

    if (!Number.isFinite(parsed)) {
      return i18n.t('validation.valueMustBeNumber')
    }
  }

  return true
}

function valueMustBePositiveNumber(v: string): boolean | TranslateResult {
  if (v) {
    const parsed = parseFloat(v)

    if (parsed <= 0) {
      return i18n.t('validation.valueMustBePositiveNumber')
    }
  }

  return true
}

@Component
export default class MenuGroupSlotEditor extends Vue {
  $refs!: {
    panel: Vue & { toggle(): void }
    form: Vue & { validate(): boolean }
  }

  @Prop({type: Object, required: true}) slotItem!: SlotCollectionResponse
  @Prop({ type: Object, required: false }) readonly selectedTopGroup!: MenuGroupList
  @Prop({ type: Array, required: false }) readonly tagList!: Tag[]

  private isFormValid: boolean = false
  private referencePrice: string | null = null
  private isSaveInProgress: boolean = false
  private name: string = ''
  private code: string = ''
  private selections: any[] = []
  private updatedSlot: any | null =null;
  private snackBar: boolean = false
  private snackBarMessage: string = ''
  private snackBarColor: string = ''

  @Action(UPDATE_SLOT) updateSlot!: { (payload: EditSlotPayload): Promise<any> }

  private referencePriceRules: Function[] = [
    valueMustBeNumber,
    valueMustBePositiveNumber
  ]

  getIcon (item: string):string | null {
    if(item && item.length > 4) {
      return item  ? `fas ${item}` : null
    } else {
      return ''
    }
  }

  onSubmitButtonClick(): void {
    this.save()
  }

  getSelectedTagClick (item: any) {
    this.selections.push(item)
  }

  async save(): Promise<void> {
    let tagCollection = this.selections.map((i: Tag) => {
      return `/api/tag/${i.id}`
    })
    const payload: EditSlotPayload = {
      id: this.slotItem.id,
      title: this.name,
      code: this.code,
      referencePrice: this.referencePrice ? parseFloat(this.referencePrice).toFixed(2) : null,
      order: this.slotItem.order,
      tags: tagCollection
    }

    this.isSaveInProgress = true

    try {
      const result = await this.updateSlot(payload)
      this.snackBar = true
      this.snackBarMessage = this.$t('settingsPage.offerStructure.saved').toString()
      this.snackBarColor = 'success'
      this.$refs.panel.toggle()
      this.$emit('slotItemSave', payload)
    } catch (e) {
      this.snackBar = true
      this.snackBarMessage = this.$t('settingsPage.snackbarErrorMessage').toString()
      this.snackBarColor = 'error'
    }finally {
      this.isSaveInProgress = false
    }
  }

  mounted () {
    this.name = this.slotItem.title
    this.code = this.slotItem.code
    this.referencePrice = this.slotItem.referencePrice
    this.slotItem.tags.forEach((item: any) => {
      const foundItem: Tag | undefined = this.tagList!.find((i: Tag) => i.id === item.id)
      if(foundItem) {
        this.selections.push(foundItem)
      }
    })

  }
}
</script>

<style lang="stylus" scoped>

</style>
